import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  WEEKDAY_ORDER,
  weeksBetween,
  hydrateAcademicCalendar,
  useAcademicCalendar,
  type AcademicCalendarWindow,
  type WeekdayName,
} from "@/lib/academicCalendar";
import { computeCalendarImpact, type AffectedWindow } from "@/lib/academicCalendarImpact";
import {
  AlertCircle, Plus, Save, Trash2, Undo2, History, Lock, Unlock, Copy, Leaf,
} from "lucide-react";

interface DraftRow extends Omit<AcademicCalendarWindow, "id"> {
  id?: string;
  _localId: string;
  _dirty?: boolean;
  _deleted?: boolean;
  _new?: boolean;
  _original?: { start_date: string; end_date: string; instructional_weeks: number; is_active: boolean };
}

interface ValidationError { localId: string; field: string; message: string; }
interface YearLock { academic_year: number; is_locked: boolean; locked_at: string | null; note: string | null; }

function newLocalId() { return `new-${Math.random().toString(36).slice(2, 9)}`; }

function blankRow(semesterKey: string, label: string, year: number): DraftRow {
  return {
    _localId: newLocalId(), _new: true, _dirty: true,
    semester_key: semesterKey, display_label: label, academic_year: year,
    weekday: "Monday", start_date: "", end_date: "",
    instructional_weeks: 12, is_active: true,
  };
}

export default function AcademicCalendarPage() {
  useAcademicCalendar();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [locks, setLocks] = useState<YearLock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<AcademicCalendarWindow[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [impact, setImpact] = useState<AffectedWindow[] | null>(null);
  const [computingImpact, setComputingImpact] = useState(false);
  const [unlockPrompt, setUnlockPrompt] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    const [w, l] = await Promise.all([
      supabase.from("academic_calendar_windows").select("*").order("semester_key").order("weekday"),
      supabase.from("academic_year_locks").select("*"),
    ]);
    if (w.error) toast({ title: "Couldn't load calendar", description: w.error.message, variant: "destructive" });
    else setRows((w.data ?? []).map((r) => rowFromDb(r as unknown as AcademicCalendarWindow)));
    setLocks((l.data ?? []) as unknown as YearLock[]);
    setLoading(false);
  }
  async function loadHistory() {
    const { data } = await supabase
      .from("academic_calendar_windows").select("*")
      .order("updated_at", { ascending: false }).limit(50);
    setHistory((data ?? []) as unknown as AcademicCalendarWindow[]);
  }
  useEffect(() => { load(); }, []);

  const dirty = rows.some((r) => r._dirty || r._deleted || r._new);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const grouped = useMemo(() => {
    const m = new Map<string, DraftRow[]>();
    for (const r of rows) {
      if (!m.has(r.semester_key)) m.set(r.semester_key, []);
      m.get(r.semester_key)!.push(r);
    }
    for (const l of m.values()) l.sort((a, b) => WEEKDAY_ORDER.indexOf(a.weekday) - WEEKDAY_ORDER.indexOf(b.weekday));
    return m;
  }, [rows]);

  const errors = useMemo(() => validate(rows), [rows]);
  const yearOf = (semKey: string) => rows.find((r) => r.semester_key === semKey)?.academic_year;
  const isYearLocked = (year: number | undefined) =>
    year !== undefined && locks.some((l) => l.academic_year === year && l.is_locked);

  function patch(localId: string, updates: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => r._localId === localId ? { ...r, ...updates, _dirty: true } : r));
  }
  function remove(localId: string) {
    setRows((prev) => prev.map((r) => r._localId === localId ? { ...r, _deleted: true } : r));
  }
  function undelete(localId: string) {
    setRows((prev) => prev.map((r) => r._localId === localId ? { ...r, _deleted: false } : r));
  }
  function addRowFor(semesterKey: string) {
    const existing = rows.find((r) => r.semester_key === semesterKey);
    setRows((prev) => [...prev, blankRow(semesterKey, existing?.display_label ?? "Semester", existing?.academic_year ?? new Date().getFullYear())]);
  }
  function addNewSemester() {
    const key = prompt("Semester key (e.g. summer_2027):")?.trim();
    if (!key) return;
    const label = prompt("Display label (e.g. Summer Semester):")?.trim() || "Semester";
    const yr = parseInt(prompt("Academic year:") || "", 10);
    if (!Number.isFinite(yr)) { toast({ title: "Invalid year", variant: "destructive" }); return; }
    setRows((prev) => [...prev, blankRow(key, label, yr)]);
  }

  async function toggleLock(year: number, nextLocked: boolean, note?: string) {
    const existing = locks.find((l) => l.academic_year === year);
    const payload = existing
      ? { is_locked: nextLocked, locked_at: nextLocked ? new Date().toISOString() : null, unlocked_at: nextLocked ? null : new Date().toISOString(), note: note ?? existing.note }
      : { academic_year: year, is_locked: nextLocked, locked_at: nextLocked ? new Date().toISOString() : null, note: note ?? null };
    const q = existing
      ? supabase.from("academic_year_locks").update(payload).eq("academic_year", year)
      : supabase.from("academic_year_locks").insert(payload as any);
    const { error } = await q;
    if (error) { toast({ title: "Couldn't change lock", description: error.message, variant: "destructive" }); return; }
    toast({ title: nextLocked ? `${year}–${year + 1} locked` : `${year}–${year + 1} unlocked` });
    load();
  }

  async function copyYear(sourceYear: number) {
    const targetYear = sourceYear + 1;
    if (rows.some((r) => r.academic_year === targetYear)) {
      if (!confirm(`Rows already exist for ${targetYear}. Continue and add more?`)) return;
    }
    const source = rows.filter((r) => r.academic_year === sourceYear && !r._deleted);
    if (source.length === 0) { toast({ title: `No windows found for ${sourceYear}` }); return; }
    const dupes: DraftRow[] = source.map((r) => ({
      _localId: newLocalId(), _new: true, _dirty: true,
      semester_key: r.semester_key.replace(String(sourceYear), String(targetYear)),
      display_label: r.display_label,
      academic_year: targetYear,
      weekday: r.weekday,
      start_date: shiftIsoYear(r.start_date, 1),
      end_date: shiftIsoYear(r.end_date, 1),
      instructional_weeks: r.instructional_weeks,
      is_active: false, // Draft
    }));
    setRows((prev) => [...prev, ...dupes]);
    toast({
      title: `Drafted ${dupes.length} windows for ${targetYear}`,
      description: "Review the dates, then Save all.",
    });
  }

  async function openImpactPreview() {
    if (errors.length > 0) { toast({ title: "Fix validation errors first", variant: "destructive" }); return; }
    // Block if any affected year is locked
    const touchedYears = new Set(rows.filter((r) => r._dirty || r._deleted || r._new).map((r) => r.academic_year));
    for (const y of touchedYears) {
      if (isYearLocked(y)) {
        toast({ title: `${y}–${y + 1} is locked`, description: "Unlock the year to make changes.", variant: "destructive" });
        return;
      }
    }
    setComputingImpact(true);
    try {
      const changes = rows.flatMap((r) => diffChange(r) as any[]) as Parameters<typeof computeCalendarImpact>[0];
      if (changes.length === 0) {
        toast({ title: "No changes to save" });
        return;
      }
      const result = await computeCalendarImpact(changes);
      setImpact(result);
    } finally { setComputingImpact(false); }
  }

  async function applyChanges() {
    setSaving(true);
    const { data: snapshot } = await supabase.from("academic_calendar_windows").select("*");
    const toDelete = rows.filter((r) => r._deleted && r.id).map((r) => r.id!);
    const toInsert = rows.filter((r) => r._new && !r._deleted).map(rowToPayload);
    const toUpdate = rows.filter((r) => r._dirty && !r._new && !r._deleted && r.id).map((r) => ({ id: r.id!, ...rowToPayload(r) }));
    try {
      if (toDelete.length) {
        const { error } = await supabase.from("academic_calendar_windows").delete().in("id", toDelete);
        if (error) throw error;
      }
      for (const u of toUpdate) {
        const { id, ...payload } = u;
        const { error } = await supabase.from("academic_calendar_windows").update(payload).eq("id", id);
        if (error) throw error;
      }
      if (toInsert.length) {
        const { error } = await supabase.from("academic_calendar_windows").insert(toInsert);
        if (error) throw error;
      }
      const { data: verify, error: vErr } = await supabase
        .from("academic_calendar_windows").select("*").order("semester_key").order("weekday");
      if (vErr) throw vErr;
      await hydrateAcademicCalendar(true);
      setRows((verify ?? []).map((r) => rowFromDb(r as unknown as AcademicCalendarWindow)));
      setImpact(null);
      toast({ title: "Semester calendar saved", description: "All connected surfaces will refresh." });
    } catch (err: any) {
      if (snapshot) {
        await supabase.from("academic_calendar_windows").delete().not("id", "is", null);
        if (snapshot.length) await supabase.from("academic_calendar_windows").insert(snapshot as any);
      }
      toast({ title: "Save failed — changes rolled back", description: err?.message ?? String(err), variant: "destructive" });
      load();
    } finally { setSaving(false); }
  }

  const knownYears = Array.from(new Set(rows.map((r) => r.academic_year))).sort();

  return (
    <AdminShell section="settings">
      <div className="container mx-auto p-4 md:p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
              <Leaf className="h-6 w-6 text-primary" /> Semester Calendar
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              The single source of truth for semester start and end dates. Every class card,
              registration form, calendar, confirmation email, and MAVIS response reads from here.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {dirty && <Badge variant="secondary" className="text-amber-700 bg-amber-50 border-amber-200">Unsaved changes</Badge>}
            <Button variant="outline" size="sm" onClick={() => { setShowHistory((v) => !v); if (!showHistory) loadHistory(); }}>
              <History className="h-4 w-4 mr-1" /> History
            </Button>
            <Button variant="outline" size="sm" onClick={addNewSemester}>
              <Plus className="h-4 w-4 mr-1" /> New semester
            </Button>
            <Button size="sm" onClick={openImpactPreview} disabled={!dirty || saving || errors.length > 0 || computingImpact}>
              <Save className="h-4 w-4 mr-1" /> {computingImpact ? "Checking…" : "Review & save"}
            </Button>
          </div>
        </div>

        {/* Year locks */}
        <Card>
          <CardHeader><CardTitle className="text-base">Academic years</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {knownYears.length === 0 && <p className="text-sm text-muted-foreground italic">No years configured yet.</p>}
            {knownYears.map((y) => {
              const locked = isYearLocked(y);
              return (
                <div key={y} className="flex items-center justify-between gap-3 p-2 rounded border bg-background">
                  <div className="flex items-center gap-3">
                    {locked ? <Lock className="h-4 w-4 text-amber-600" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
                    <span className="font-medium">{y}–{y + 1} Academic Year</span>
                    <Badge variant={locked ? "default" : "secondary"} className={locked ? "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100" : ""}>
                      {locked ? "Locked" : "Editable"}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyYear(y)}>
                      <Copy className="h-4 w-4 mr-1" /> Create next year ({y + 1}–{y + 2})
                    </Button>
                    {locked ? (
                      <Button variant="outline" size="sm" onClick={() => setUnlockPrompt(y)}>
                        <Unlock className="h-4 w-4 mr-1" /> Unlock for editing
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => toggleLock(y, true)}>
                        <Lock className="h-4 w-4 mr-1" /> Lock
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {errors.length > 0 && (
          <Card className="border-destructive/50">
            <CardContent className="p-3 text-sm text-destructive space-y-1">
              <div className="flex items-center gap-2 font-medium"><AlertCircle className="h-4 w-4" /> Fix these before saving:</div>
              <ul className="list-disc pl-6">
                {errors.slice(0, 8).map((e, i) => <li key={i}>{e.message}</li>)}
                {errors.length > 8 && <li>… and {errors.length - 8} more</li>}
              </ul>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          Array.from(grouped.entries()).map(([key, list]) => (
            <SemesterCard
              key={key} semesterKey={key} rows={list} errors={errors}
              locked={isYearLocked(yearOf(key))}
              onPatch={patch} onDelete={remove} onUndelete={undelete}
              onAddRow={() => addRowFor(key)}
            />
          ))
        )}

        {showHistory && (
          <Card>
            <CardHeader><CardTitle className="text-base">Recent changes</CardTitle></CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              {history.length === 0 ? "No history yet." : history.map((h) => (
                <div key={h.id}>
                  <span className="text-foreground">{h.display_label}</span> · {h.weekday} · {h.start_date} → {h.end_date}
                  {h.updated_at && <span className="ml-2">({new Date(h.updated_at).toLocaleString()})</span>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Impact preview dialog */}
      <Dialog open={!!impact} onOpenChange={(o) => !o && setImpact(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review changes before saving</DialogTitle>
            <DialogDescription>Here's what these calendar edits will touch.</DialogDescription>
          </DialogHeader>
          <ImpactBody rows={impact ?? []} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpact(null)} disabled={saving}>Cancel</Button>
            <Button onClick={applyChanges} disabled={saving}>{saving ? "Saving…" : "Apply changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock confirmation */}
      <Dialog open={unlockPrompt !== null} onOpenChange={(o) => !o && setUnlockPrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock {unlockPrompt}–{(unlockPrompt ?? 0) + 1}?</DialogTitle>
            <DialogDescription>
              Unlocking allows edits to every semester window in this academic year. Only do this if you're sure.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockPrompt(null)}>Cancel</Button>
            <Button onClick={() => { if (unlockPrompt !== null) toggleLock(unlockPrompt, false); setUnlockPrompt(null); }}>
              Yes, unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function ImpactBody({ rows }: { rows: AffectedWindow[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No changes.</p>;
  const bySemester = new Map<string, AffectedWindow[]>();
  for (const r of rows) {
    if (!bySemester.has(r.semester_label)) bySemester.set(r.semester_label, []);
    bySemester.get(r.semester_label)!.push(r);
  }
  return (
    <div className="space-y-4 text-sm">
      {Array.from(bySemester.entries()).map(([label, list]) => (
        <div key={label} className="space-y-2">
          <div className="font-medium flex items-center gap-2">🍂 {label}</div>
          {list.map((w, i) => (
            <div key={i} className="ml-4 pl-3 border-l border-border space-y-1">
              <div className="font-medium">
                {w.weekday}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {w.change === "created" ? "New window" : w.change === "deleted" ? "Removing window" : "Date change"}
                </Badge>
              </div>
              {w.before && w.after && (
                <div className="text-xs text-muted-foreground">
                  {w.before.start} → {w.before.end} ({w.before.weeks}w)
                  {" "}⟶{" "}
                  <span className="text-foreground">{w.after.start} → {w.after.end} ({w.after.weeks}w)</span>
                </div>
              )}
              {w.change === "created" && w.after && (
                <div className="text-xs text-muted-foreground">New: {w.after.start} → {w.after.end} ({w.after.weeks}w)</div>
              )}
              {w.change === "deleted" && w.before && (
                <div className="text-xs text-muted-foreground">Removing: {w.before.start} → {w.before.end}</div>
              )}
              <div className="text-xs">
                <span className="text-muted-foreground">Affects</span>{" "}
                <strong>{w.classes.length}</strong> {w.classes.length === 1 ? "class" : "classes"} ·{" "}
                <strong>{w.registrations}</strong> registration{w.registrations === 1 ? "" : "s"} ·{" "}
                <strong>{w.interest}</strong> interest form{w.interest === 1 ? "" : "s"}
              </div>
              {w.classes.length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc ml-5">
                  {w.classes.slice(0, 8).map((c) => <li key={c.id}>{c.title}</li>)}
                  {w.classes.length > 8 && <li>… and {w.classes.length - 8} more</li>}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function SemesterCard({
  semesterKey, rows, errors, locked, onPatch, onDelete, onUndelete, onAddRow,
}: {
  semesterKey: string;
  rows: DraftRow[];
  errors: ValidationError[];
  locked: boolean;
  onPatch: (id: string, u: Partial<DraftRow>) => void;
  onDelete: (id: string) => void;
  onUndelete: (id: string) => void;
  onAddRow: () => void;
}) {
  const activeRow = rows.find((r) => !r._deleted);
  const label = activeRow?.display_label ?? "Semester";
  const year = activeRow?.academic_year;
  return (
    <Card className={locked ? "opacity-95" : ""}>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            {locked && <Lock className="h-4 w-4 text-amber-600" />}
            {label} {year && <span className="text-muted-foreground font-normal">· {year}</span>}
          </span>
          <span className="text-xs text-muted-foreground font-mono">{semesterKey}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {locked && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            This academic year is locked. Unlock it above to make changes.
          </div>
        )}
        {rows.length === 0 && <p className="text-sm text-muted-foreground italic">No windows configured yet.</p>}
        {rows.map((r) => {
          const rowErrs = errors.filter((e) => e.localId === r._localId);
          const computedWeeks = r.start_date && r.end_date ? weeksBetween(r.start_date, r.end_date) : null;
          const weekMismatch = computedWeeks !== null && r.instructional_weeks !== computedWeeks;
          const disabled = r._deleted || locked;
          return (
            <div
              key={r._localId}
              className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto_auto] gap-2 items-end p-3 rounded border ${r._deleted ? "opacity-50 bg-muted/40" : "bg-background"} ${rowErrs.length ? "border-destructive/50" : "border-border"}`}
            >
              <div>
                <Label className="text-xs">Label</Label>
                <Input value={r.display_label} disabled={disabled}
                  onChange={(e) => onPatch(r._localId, { display_label: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Academic year</Label>
                <Input type="number" value={r.academic_year} disabled={disabled}
                  onChange={(e) => onPatch(r._localId, { academic_year: parseInt(e.target.value, 10) })} />
              </div>
              <div>
                <Label className="text-xs">Weekday</Label>
                <Select value={r.weekday} disabled={disabled}
                  onValueChange={(v) => onPatch(r._localId, { weekday: v as WeekdayName })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAY_ORDER.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={r.start_date} disabled={disabled}
                  onChange={(e) => onPatch(r._localId, { start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">End date</Label>
                <Input type="date" value={r.end_date} disabled={disabled}
                  onChange={(e) => onPatch(r._localId, { end_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Weeks</Label>
                <Input type="number" min={1} value={r.instructional_weeks} disabled={disabled}
                  onChange={(e) => onPatch(r._localId, { instructional_weeks: parseInt(e.target.value, 10) })}
                  className={weekMismatch ? "border-amber-500" : ""} />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex flex-col items-center">
                  <Label className="text-xs">Active</Label>
                  <Switch checked={r.is_active} disabled={disabled}
                    onCheckedChange={(v) => onPatch(r._localId, { is_active: v })} />
                </div>
                {r._deleted ? (
                  <Button size="icon" variant="ghost" onClick={() => onUndelete(r._localId)} disabled={locked}><Undo2 className="h-4 w-4" /></Button>
                ) : (
                  <Button size="icon" variant="ghost" onClick={() => onDelete(r._localId)} disabled={locked}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
              {(rowErrs.length > 0 || weekMismatch) && (
                <div className="md:col-span-full text-xs space-y-0.5">
                  {rowErrs.map((e, i) => <div key={i} className="text-destructive">• {e.message}</div>)}
                  {weekMismatch && !rowErrs.length && (
                    <div className="text-amber-600">
                      • Dates span {computedWeeks} weeks but instructional weeks is set to {r.instructional_weeks}.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <Separator />
        <Button variant="outline" size="sm" onClick={onAddRow} disabled={locked}>
          <Plus className="h-4 w-4 mr-1" /> Add weekday window
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function rowFromDb(r: AcademicCalendarWindow): DraftRow {
  return {
    ...r, _localId: r.id,
    _original: {
      start_date: r.start_date, end_date: r.end_date,
      instructional_weeks: r.instructional_weeks, is_active: r.is_active,
    },
  };
}
function rowToPayload(r: DraftRow) {
  return {
    semester_key: r.semester_key, display_label: r.display_label, academic_year: r.academic_year,
    weekday: r.weekday, start_date: r.start_date, end_date: r.end_date,
    instructional_weeks: r.instructional_weeks, is_active: r.is_active,
  };
}
function shiftIsoYear(iso: string, delta: number): string {
  if (!iso) return iso;
  const [y, m, d] = iso.split("-");
  return `${parseInt(y, 10) + delta}-${m}-${d}`;
}
function diffChange(r: DraftRow) {
  if (r._new && !r._deleted) {
    return [{
      semester_key: r.semester_key, semester_label: r.display_label, weekday: r.weekday,
      change: "created" as const,
      after: { start: r.start_date, end: r.end_date, weeks: r.instructional_weeks },
    }];
  }
  if (r._deleted && r.id) {
    return [{
      semester_key: r.semester_key, semester_label: r.display_label, weekday: r.weekday,
      change: "deleted" as const,
      before: r._original ? { start: r._original.start_date, end: r._original.end_date, weeks: r._original.instructional_weeks } : undefined,
    }];
  }
  if (r._dirty && r._original) {
    const changed =
      r.start_date !== r._original.start_date ||
      r.end_date !== r._original.end_date ||
      r.instructional_weeks !== r._original.instructional_weeks ||
      r.is_active !== r._original.is_active;
    if (!changed) return [];
    return [{
      semester_key: r.semester_key, semester_label: r.display_label, weekday: r.weekday,
      change: "updated" as const,
      before: { start: r._original.start_date, end: r._original.end_date, weeks: r._original.instructional_weeks },
      after: { start: r.start_date, end: r.end_date, weeks: r.instructional_weeks },
    }];
  }
  return [];
}
function validate(rows: DraftRow[]): ValidationError[] {
  const errs: ValidationError[] = [];
  const activeKey = new Map<string, string[]>();
  for (const r of rows) {
    if (r._deleted) continue;
    if (!r.semester_key) errs.push({ localId: r._localId, field: "semester_key", message: "Missing semester key." });
    if (!r.weekday) errs.push({ localId: r._localId, field: "weekday", message: "Missing weekday." });
    if (!r.academic_year || !Number.isFinite(r.academic_year))
      errs.push({ localId: r._localId, field: "academic_year", message: "Missing academic year." });
    if (!r.start_date) errs.push({ localId: r._localId, field: "start_date", message: "Missing start date." });
    if (!r.end_date) errs.push({ localId: r._localId, field: "end_date", message: "Missing end date." });
    if (r.start_date && r.end_date && r.end_date < r.start_date)
      errs.push({ localId: r._localId, field: "end_date", message: `${r.weekday}: end date is before start date.` });
    if (!r.instructional_weeks || r.instructional_weeks < 1)
      errs.push({ localId: r._localId, field: "instructional_weeks", message: `${r.weekday}: instructional weeks must be 1 or more.` });
    if (r.is_active) {
      const k = `${r.semester_key}|${r.weekday}`;
      if (!activeKey.has(k)) activeKey.set(k, []);
      activeKey.get(k)!.push(r._localId);
    }
  }
  for (const [k, ids] of activeKey) {
    if (ids.length > 1) {
      const [sem, wd] = k.split("|");
      for (const id of ids) errs.push({ localId: id, field: "weekday", message: `Duplicate active window for ${sem} ${wd}.` });
    }
  }
  return errs;
}
